import { useCallback, useEffect, useRef, useState } from 'react';
import { OutreachAction, SupplierThread } from '../components/forms/outreach/types';
import { readOutreachNote } from '../utils/outreachNotes';
import { threadIdentity } from './outreachActionModel';
import { useOutreachActions } from './useOutreachActions';
import { ThreadSendResult } from './useOutreachThreadSend';

/**
 * The buyer's box on the open thread: what is typed in it, what Robbie has put
 * in it, and the one send that empties it.
 *
 * Nothing a card does on an open conversation reaches a supplier — a card
 * writes its ask in here instead, and the asks accumulate as the buyer taps
 * more of them. The send is what makes those cards done; emptying the box
 * instead drops the staging, because no card may claim to be sitting in a box
 * that is blank.
 *
 * Every thread keeps its OWN box, keyed the same way its staging is: half a
 * query typed at one supplier must not surface in the next one's composer, and
 * the asks staged on a thread have to still be there — text and tags together —
 * when the buyer comes back to it.
 */
interface OutreachComposerOptions {
  activeThread: SupplierThread | undefined;
  bomId: string | null;
  actions: ReturnType<typeof useOutreachActions>;
  /** The whole send: Robbie writes the email from the query and dispatches it. */
  sendAsk: (thread: SupplierThread, ask: string, isNegotiation: boolean) => Promise<ThreadSendResult>;
  /** A send is in flight on THIS thread; other threads stay free. */
  isSending: boolean;
}

export const useOutreachComposer = ({ activeThread, bomId, actions, sendAsk, isSending }: OutreachComposerOptions) => {
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const threadKey = activeThread ? threadIdentity(activeThread) : '';
  const messageInput = drafts[threadKey] ?? '';

  const writeDraft = (next: (prev: string) => string) =>
    setDrafts((prev) => ({ ...prev, [threadKey]: next(prev[threadKey] ?? '') }));

  // Discovery can queue a worth-asking note for a supplier's FIRST message —
  // surface it once per thread, never over typed text.
  const notePrefillRef = useRef<string | null>(null);
  useEffect(() => {
    if (!activeThread || !bomId || notePrefillRef.current === activeThread.id) return;
    notePrefillRef.current = activeThread.id;
    if (activeThread.messages.length > 0 || !activeThread.componentId) return;
    const note = readOutreachNote(bomId, activeThread.componentId, activeThread.supplierName);
    if (!note) return;
    const key = threadIdentity(activeThread);
    setDrafts((prev) => (prev[key] ? prev : { ...prev, [key]: note }));
  }, [activeThread, bomId]);

  /**
   * The typed query goes out now. Robbie writes the email from it and sends in
   * the same round-trip. The words move into the thread as a pending bubble
   * at once, so the box empties then; a send that never left the browser puts
   * nothing in the thread and keeps the box. Staged asks close only on a real
   * send — a failed one stays in the thread with its own retry instead.
   */
  const handleSendMessage = async () => {
    if (!messageInput.trim() || !activeThread || isSending) return;
    const thread = activeThread;
    const key = threadIdentity(thread);
    const typed = messageInput;
    setDrafts((prev) => ({ ...prev, [key]: '' }));
    const result = await sendAsk(thread, typed.trim(), false);
    if (result === 'rejected') {
      setDrafts((prev) => (prev[key] ? prev : { ...prev, [key]: typed }));
      return;
    }
    if (result === 'sent') await actions.resolveStagedSend(thread);
    else actions.clearStaging(thread);
  };

  /**
   * Cards put their asks in the box: one card's, or every one still out of it.
   * They land as the buyer's own draft, under anything already typed, and an
   * ask already in the box is never written twice. Nothing leaves here.
   */
  const stageActions = (chosen: OutreachAction[], approvedPrice?: number) => {
    if (!activeThread) return;
    const query = actions.stageActions(activeThread, chosen, approvedPrice);
    if (!query) return;
    writeDraft((prev) => (prev.trim() ? `${prev.trimEnd()}\n\n${query}` : query));
  };

  /**
   * A Robbie draft the buyer put in the composer: into THAT thread's box,
   * whichever is open. Nothing is sent — the buyer's Send still is.
   */
  const fillDraft = useCallback((thread: SupplierThread, text: string, mode: 'replace' | 'append') => {
    const key = threadIdentity(thread);
    setDrafts((prev) => {
      const current = prev[key] ?? '';
      return { ...prev, [key]: mode === 'append' && current.trim() ? `${current.trimEnd()}\n\n${text}` : text };
    });
  }, []);

  /** Emptying the box empties the staging with it — see the hook's note above. */
  const editMessageInput = (value: string) => {
    writeDraft(() => value);
    if (!value.trim() && activeThread) actions.clearStaging(activeThread);
  };

  return {
    messageInput,
    setMessageInput: editMessageInput,
    stageActions,
    fillDraft,
    stagedActionIds: actions.stagedIdsFor(activeThread),
    handleSendMessage,
  };
};
